import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Android, Apple, Shield, VerifiedUser, Block } from '@mui/icons-material';

const steps = ['Plan', 'Account', 'Verify', 'Domain', 'Admin', 'DNS', 'Complete'];

interface SetupLayoutProps {
  activeStep: number;
  backPath?: string;
  children: React.ReactNode;
  hideBack?: boolean;
  hideStepper?: boolean;
}

const SetupLayout: React.FC<SetupLayoutProps> = ({ activeStep, backPath, children, hideBack, hideStepper }) => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkSx = {
    fontSize: '0.875rem', fontWeight: 500, color: '#374151', textDecoration: 'none',
    cursor: 'pointer', transition: 'color 0.2s',
    '&:hover': { color: '#dc2626' },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', fontFamily: 'Inter, system-ui, sans-serif' }}>
      {/* Navbar - matching landing page */}
      <Box sx={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        bgcolor: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        boxShadow: scrolled ? '0 1px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s',
      }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {!hideBack && backPath && (
              <IconButton onClick={() => navigate(backPath)} sx={{ color: '#64748b', p: 0.5 }}>
                <ArrowBackIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', cursor: 'pointer' }} onClick={() => navigate('/login')}>
              <Box component="img" src="/logo-hd.png" alt="CruvzMail" sx={{ height: 77 }} />
              <Typography sx={{ fontSize: '0.7rem', color: '#6b7280', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2.5, letterSpacing: '0.02em', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale' }}>Where Business Speaks!</Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 3.5 }}>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/enterprise')}>For Business</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/features')}>Features</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/migration')}>Migration</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/signup')}>Plans and Pricing</Typography>
            <Typography component="a" sx={navLinkSx} onClick={() => navigate('/help-center')}>Support</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button onClick={() => navigate('/login')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#374151', border: '1.5px solid #e5e7eb',
              '&:hover': { borderColor: '#dc2626', color: '#dc2626', bgcolor: '#fef2f2' },
            }}>Sign In</Button>
            <Button onClick={() => navigate('/signup')} sx={{
              px: 2.5, py: 1, borderRadius: '8px', fontSize: '0.875rem', fontWeight: 600, textTransform: 'none',
              color: '#fff', bgcolor: '#dc2626', boxShadow: '0 2px 8px rgba(220,38,38,0.25)',
              '&:hover': { bgcolor: '#b91c1c', boxShadow: '0 4px 16px rgba(220,38,38,0.35)', transform: 'translateY(-1px)' },
            }}>Get Started Free</Button>
          </Box>
        </Box>
      </Box>

      {/* Stepper */}
      {!hideStepper && (
        <Box sx={{ bgcolor: '#fafafa', py: 2, borderBottom: '1px solid #f0f0f0', mt: '72px' }}>
          <Box sx={{ maxWidth: 720, mx: 'auto', px: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{
              '& .MuiStepConnector-line': { borderColor: '#e5e7eb', borderTopWidth: 2 },
              '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line': { borderColor: '#dc2626' },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#22c55e' },
              '& .MuiStepLabel-label': {
                color: '#9ca3af',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '0.7rem',
                fontWeight: 500,
                mt: 0.5,
              },
              '& .MuiStepLabel-label.Mui-active': { color: '#111827', fontWeight: 600 },
              '& .MuiStepLabel-label.Mui-completed': { color: '#22c55e', fontWeight: 500 },
              '& .MuiStepIcon-root': { color: '#e5e7eb', fontSize: '1.5rem' },
              '& .MuiStepIcon-root.Mui-active': { color: '#dc2626' },
              '& .MuiStepIcon-root.Mui-completed': { color: '#22c55e' },
              '& .MuiStepIcon-text': { fontSize: '0.7rem', fontWeight: 700 },
            }}>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>
          </Box>
        </Box>
      )}

      {/* Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', ...(hideStepper ? { mt: '72px' } : {}) }}>
        {children}
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: '#0a0f1a', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(220,38,38,0.3), rgba(59,130,246,0.3), transparent)' },
      }}>
        {/* Top CTA strip */}
        <Box sx={{ py: { xs: 5, md: 6 }, px: { xs: 2, md: 5 }, background: 'linear-gradient(135deg, rgba(220,38,38,0.08) 0%, rgba(15,23,42,0) 50%, rgba(59,130,246,0.06) 100%)' }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'center', justifyContent: 'space-between', gap: 3 }}>
            <Box>
              <Typography sx={{ fontSize: { xs: '1.3rem', md: '1.6rem' }, fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-0.02em' }}>Ready to upgrade your business email?</Typography>
              <Typography sx={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)' }}>Join thousands of businesses already using CruvzMail. Free plan available.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
              <Button onClick={() => navigate('/signup')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 700, textTransform: 'none',
                color: '#fff', background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                boxShadow: '0 4px 20px rgba(220,38,38,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #ef4444, #dc2626)', transform: 'translateY(-1px)', boxShadow: '0 6px 24px rgba(220,38,38,0.4)' },
                transition: 'all 0.3s',
              }}>Start Free Trial</Button>
              <Button onClick={() => navigate('/contact')} sx={{
                px: 3.5, py: 1.5, borderRadius: '12px', fontSize: '0.9rem', fontWeight: 600, textTransform: 'none',
                color: 'rgba(255,255,255,0.8)', border: '1px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' },
                transition: 'all 0.3s',
              }}>Contact Sales</Button>
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Main footer content */}
        <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: { xs: 6, lg: 10 } }}>
            {/* Brand column */}
            <Box sx={{ flex: '0 0 320px', maxWidth: { xs: '100%', lg: 320 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                <Box component="img" src="/Cruvzmail_Footer_logo.png" alt="CruvzMail" sx={{ height: 80, alignSelf: 'flex-start' }} />
                <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', fontFamily: 'Georgia, "Times New Roman", serif', fontWeight: 600, ml: '88px', mt: -2, letterSpacing: '0.02em' }}>Where Business Speaks!</Typography>
              </Box>
              <Typography sx={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, mb: 3 }}>
                Professional email hosting built for modern businesses. Self-hosted, secure, and fully customizable with your brand.
              </Typography>
              {/* App download badges */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                <Button href="/CruvzMail-v1.0.apk" download sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'none',
                  color: '#fff', bgcolor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.14)', borderColor: 'rgba(255,255,255,0.25)' },
                  transition: 'all 0.25s', display: 'flex', alignItems: 'center', gap: 1,
                }} startIcon={<Android sx={{ fontSize: 20 }} />}>
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1 }}>Download on</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>Android</Typography>
                  </Box>
                </Button>
                <Box sx={{
                  px: 2.5, py: 1.25, borderRadius: '10px', display: 'flex', alignItems: 'center', gap: 1,
                  bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', opacity: 0.5,
                }}>
                  <Apple sx={{ fontSize: 20, color: 'rgba(255,255,255,0.4)' }} />
                  <Box sx={{ textAlign: 'left' }}>
                    <Typography sx={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>Coming soon</Typography>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: 'rgba(255,255,255,0.4)', lineHeight: 1.2 }}>iOS</Typography>
                  </Box>
                </Box>
              </Box>
              {/* Trust badges */}
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {[
                  { icon: <Shield sx={{ fontSize: 14 }} />, text: '256-bit encryption' },
                  { icon: <VerifiedUser sx={{ fontSize: 14 }} />, text: '99.9% uptime' },
                  { icon: <Block sx={{ fontSize: 14 }} />, text: 'Zero ads' },
                ].map((badge) => (
                  <Box key={badge.text} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, '& svg': { color: 'rgba(255,255,255,0.25)' } }}>
                    {badge.icon}
                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)', fontWeight: 500 }}>{badge.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Link columns */}
            <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: { xs: 4, md: 3 } }}>
              {[
                { title: 'Product', links: [
                  { label: 'Features', href: '/features' }, { label: 'Pricing', href: '/signup' },
                  { label: 'Enterprise', href: '/enterprise' }, { label: 'Migration', href: '/migration' },
                  { label: 'Security', href: '/features' }, { label: 'Mobile App', href: '/CruvzMail-v1.0.apk' },
                ]},
                { title: 'Solutions', links: [
                  { label: 'Small Business', href: '/signup' }, { label: 'Enterprise', href: '/enterprise' },
                  { label: 'Startups', href: '/signup' }, { label: 'Education', href: '/signup' },
                  { label: 'Healthcare', href: '/signup' }, { label: 'Non-Profit', href: '/signup' },
                ]},
                { title: 'Company', links: [
                  { label: 'About Us', href: '/about' }, { label: 'Blog', href: '/blog' },
                  { label: 'Careers', href: '/careers' }, { label: 'Contact', href: '/contact' },
                  { label: 'Press Kit', href: '/about' }, { label: 'Partners', href: '/contact' },
                ]},
                { title: 'Resources', links: [
                  { label: 'Help Center', href: '/help-center' }, { label: 'Documentation', href: '/documentation' },
                  { label: 'API Reference', href: '/documentation' }, { label: 'System Status', href: '/status' },
                  { label: 'Community', href: '/community' }, { label: 'Webinars', href: '/blog' },
                ]},
              ].map((col) => (
                <Box key={col.title}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.85)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 2.5 }}>{col.title}</Typography>
                  {col.links.map((link) => (
                    <Typography
                      key={link.label}
                      component="a"
                      href={link.href}
                      onClick={(e: React.MouseEvent) => { if (!link.href.endsWith('.apk')) { e.preventDefault(); navigate(link.href); } }}
                      sx={{ display: 'block', fontSize: '0.82rem', color: 'rgba(255,255,255,0.4)', textDecoration: 'none', mb: 1.5, transition: 'all 0.2s', '&:hover': { color: '#fca5a5', transform: 'translateX(2px)' } }}
                    >
                      {link.label}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Divider */}
        <Box sx={{ maxWidth: 1280, mx: 'auto', px: { xs: 2, md: 5 } }}>
          <Box sx={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)' }} />
        </Box>

        {/* Bottom bar */}
        <Box sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, md: 5 } }}>
          <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', justifyContent: { xs: 'center', md: 'flex-start' } }}>
              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>© {new Date().getFullYear()} CruvzMail by Cruvz Online Shopping India Pvt Ltd. All rights reserved.</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'center' }}>
              {[
                { label: 'Privacy Policy', href: '/privacy-policy' },
                { label: 'Terms of Service', href: '/terms-of-service' },
                { label: 'Cookie Policy', href: '/cookie-policy' },
                { label: 'SLA', href: '/terms-of-service' },
                { label: 'Sitemap', href: '/features' },
              ].map((l, i) => (
                <React.Fragment key={l.label}>
                  {i > 0 && <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.15)' }} />}
                  <Typography component="a" href={l.href} onClick={(e: React.MouseEvent) => { e.preventDefault(); navigate(l.href); }} sx={{ fontSize: '0.73rem', color: 'rgba(255,255,255,0.3)', textDecoration: 'none', transition: 'color 0.2s', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}>{l.label}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.5)', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
              <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500 }}>All systems operational</Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SetupLayout;
export { steps };
